/* ============================================================
 * src/layouts/Shell.tsx
 *
 * ESM transcription of the v.3 prototype's shell.jsx. The original
 * hash-router (`useRoute` + `window.navigate`) is replaced with
 * react-router-dom's `useLocation` + `useNavigate`. Sidebar, Header,
 * Command Palette and Notifications Drawer are preserved verbatim
 * apart from the routing edits.
 *
 * Public routes (sign-in, locked, etc.) render bare (no chrome) per
 * the prototype's `PUBLIC` array.
 * ============================================================ */
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import {
  Icon, Btn, Pill, Avatar, Dropdown, Breadcrumbs, EmptyState,
  Modal as _Modal, SlideOver, ToastProvider, useToast, TONES,
} from '../components/primitives';
import { CIRCA } from '../mock/circa';
import { fmtDur, ago } from '../lib/format';
import { subscribeNavigate } from '../lib/nav';

/* avoid unused-import lint */
void _Modal;

/* ---------------- Navigation config ---------------- */
type NavItem = { label: string; icon: string; path: string; badge?: number };
type NavSection = { heading: string; items: NavItem[] };

export const NAV: NavSection[] = [
  { heading: 'Principal', items: [
    { label: 'Panel', icon: 'home', path: '/' },
    { label: 'Monitor en vivo', icon: 'radio', path: '/monitoring' },
  ]},
  { heading: 'Canales', items: [
    { label: 'Canales', icon: 'tv', path: '/channels' },
    { label: 'Destinos de publicación', icon: 'send', path: '/destinations' },
    { label: 'Capacidad de publicación', icon: 'layers', path: '/publishing-capacity' },
  ]},
  { heading: 'Contenido', items: [
    { label: 'Biblioteca', icon: 'film', path: '/library' },
    { label: 'Bandeja editorial', icon: 'message', path: '/editorial-inbox' },
    { label: 'Fuentes de contenido', icon: 'database', path: '/feed-sources' },
    { label: 'Salud del feed', icon: 'radio', path: '/feed-health' },
    { label: 'Escenas', icon: 'grid', path: '/library/scenes' },
    { label: 'Búsqueda inteligente', icon: 'sparkles', path: '/search' },
  ]},
  { heading: 'Programación', items: [
    { label: 'Programaciones', icon: 'calendar', path: '/schedules' },
    { label: 'Plantillas de cortes', icon: 'layers', path: '/templates' },
    { label: 'Guía de programación', icon: 'fileText', path: '/programme-guide' },
  ]},
  { heading: 'Análisis', items: [
    { label: 'Analíticas', icon: 'chart', path: '/analytics' },
    { label: 'Calidad de visión', icon: 'gauge', path: '/quality' },
    { label: 'Costos', icon: 'dollar', path: '/cost' },
    { label: 'Disponibilidad', icon: 'activity', path: '/uptime' },
    { label: 'Salud del reproductor', icon: 'signal', path: '/player-health' },
    { label: 'Entrega de anuncios', icon: 'zap', path: '/ad-performance' },
    { label: 'Gasto por proveedor', icon: 'dollar', path: '/provider-spend' },
  ]},
  { heading: 'Operaciones', items: [
    { label: 'Salud del sistema', icon: 'server', path: '/system-health' },
    { label: 'Automatizaciones', icon: 'bolt', path: '/automations' },
    { label: 'Alertas', icon: 'bell', path: '/alerts', badge: CIRCA.counts.alerts },
    { label: 'Operaciones masivas', icon: 'sliders', path: '/batch-config' },
    { label: 'Cumplimiento', icon: 'shield', path: '/compliance' },
    { label: 'Historial de versiones', icon: 'clock', path: '/version-history' },
    { label: 'Vista del espectador', icon: 'eye', path: '/viewer-preview' },
  ]},
];

const PUBLIC = ['/sign-in', '/sign-up', '/verify-email', '/forgot-password', '/onboarding', '/locked', '/session-expired', '/operator'];

export function Wordmark({ size = 24, onClick }: { size?: number; onClick?: () => void }) {
  return (
    <span onClick={onClick} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, cursor: onClick ? 'pointer' : 'default',
      fontFamily: 'var(--font-display)', fontSize: size, fontWeight: 500, letterSpacing: '-0.02em', color: 'var(--ink)' }}>
      Circa<span style={{ width: size * 0.16, height: size * 0.16, borderRadius: '50%', background: 'var(--live)', alignSelf: 'flex-end', marginBottom: size * 0.12 }} />
    </span>
  );
}

function Sidebar({ path, onClose, mobile }: { path: string; onClose?: () => void; mobile?: boolean }) {
  const navigate = useNavigate();
  return (
    <aside style={{ width: 248, flexShrink: 0, height: '100vh', position: mobile ? 'fixed' : 'sticky', top: 0, left: 0,
      background: 'var(--canvas)', borderRight: '1px solid var(--border-cool)', display: 'flex', flexDirection: 'column',
      zIndex: mobile ? 1100 : 10, animation: mobile ? 'circa-slide-in-right .2s' : undefined }}>
      <div style={{ padding: '22px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Wordmark onClick={() => { navigate('/'); onClose && onClose(); }} />
        {mobile && <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-slate)' }}><Icon name="x" size={20} /></button>}
      </div>
      <nav style={{ flex: 1, overflow: 'auto', padding: '4px 12px 24px' }}>
        {NAV.map(sec => (
          <div key={sec.heading} style={{ marginBottom: 18 }}>
            <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', padding: '0 12px 8px', letterSpacing: '0.06em' }}>{sec.heading}</div>
            {sec.items.map(it => {
              const on = it.path === '/' ? path === '/' : path.startsWith(it.path);
              return (
                <a key={it.path} href={it.path} onClick={(e) => { e.preventDefault(); navigate(it.path); onClose && onClose(); }}
                  className="circa-nav-link" data-on={on ? '1' : '0'}
                  style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 12px', borderRadius: 8, marginBottom: 1,
                    textDecoration: 'none', fontSize: 14, color: on ? 'var(--ink)' : 'var(--body-muted)',
                    background: on ? 'var(--pale-green)' : 'transparent', fontWeight: on ? 500 : 400, transition: 'background .12s' }}>
                  <Icon name={it.icon} size={17} color={on ? 'var(--deep-green)' : 'var(--muted-slate)'} />
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {it.badge ? <Pill tone="bad" size="sm" style={{ padding: '1px 7px' }}>{it.badge}</Pill> : null}
                </a>
              );
            })}
          </div>
        ))}
      </nav>
      <div style={{ padding: 12, borderTop: '1px solid var(--lightest-gray)' }}>
        <a href="/settings" onClick={(e) => { e.preventDefault(); navigate('/settings'); onClose && onClose(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 12px', borderRadius: 8, textDecoration: 'none',
            fontSize: 14, color: path.startsWith('/settings') ? 'var(--ink)' : 'var(--body-muted)',
            background: path.startsWith('/settings') ? 'var(--pale-green)' : 'transparent' }}>
          <Icon name="settings" size={17} color="var(--muted-slate)" /><span>Configuración</span>
        </a>
      </div>
    </aside>
  );
}

function useBreadcrumbs(path: string) {
  const parts = path.split('/').filter(Boolean);
  const labelFor = (seg: string) => {
    const flat = NAV.flatMap(s => s.items);
    const m = flat.find(i => i.path === '/' + seg);
    if (m) return m.label;
    const map: Record<string, string> = { settings: 'Configuración', channels: 'Canales', library: 'Contenido', schedules: 'Programaciones',
      new: 'Nuevo', edit: 'Editar', 'go-live': 'Salir al aire', monitoring: 'Monitor en vivo', analytics: 'Analíticas',
      members: 'Miembros', organization: 'Organización', branding: 'Marca', security: 'Seguridad', billing: 'Facturación',
      profile: 'Perfil', advanced: 'Avanzado', automations: 'Automatizaciones', alerts: 'Alertas' };
    return map[seg] || seg;
  };
  const crumbs: Array<{ label: string; href?: string }> = [{ label: 'Circa', href: '/' }];
  let acc = '';
  parts.forEach(p => { acc += '/' + p; crumbs.push({ label: labelFor(p), href: acc }); });
  if (path === '/') return [{ label: 'Panel' }];
  return crumbs;
}

function Header({ path, onMenu, onSearch, onBell }: { path: string; onMenu: () => void; onSearch: () => void; onBell: () => void }) {
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const crumbs = useBreadcrumbs(path);

  // T311 / FR-002 — Real sign-out. The Dropdown item below calls
  // useAuthActions().signOut() (returns a Promise) and then redirects to
  // /sign-in via the React Router hook. Any post-signOut redirect through
  // the legacy lib/nav shim is forbidden — Shell is the route-aware site
  // and must use the hook directly.
  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <header style={{ height: 56, flexShrink: 0, background: 'var(--canvas)', borderBottom: '1px solid var(--border-cool)',
      position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <button className="circa-hamburger" onClick={onMenu} aria-label="Menú" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-muted)', display: 'none', padding: 4 }}><Icon name="menu" size={20} /></button>
        <Breadcrumbs items={crumbs} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onSearch} aria-label="Buscar" style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border-cool)',
          background: 'var(--canvas)', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: 'var(--muted-slate)', fontSize: 13, fontFamily: 'var(--font-sans)' }}>
          <Icon name="search" size={15} /><span className="circa-searchhint">Buscar</span>
          <span className="t-mono circa-searchhint" style={{ fontSize: 10, background: 'var(--lightest-gray)', padding: '2px 5px', borderRadius: 4 }}>⌘K</span>
        </button>
        <button onClick={onBell} aria-label="Notificaciones" style={{ position: 'relative', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-muted)', padding: 6 }}>
          <Icon name="bell" size={19} />
          {CIRCA.counts.unread > 0 && <span style={{ position: 'absolute', top: 3, right: 3, width: 8, height: 8, borderRadius: '50%', background: 'var(--bad)', border: '1.5px solid var(--canvas)' }} />}
        </button>
        <Dropdown align="right" trigger={<button aria-label="Cuenta" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}><Avatar name={CIRCA.member.name} initials={CIRCA.member.initials} size={32} /></button>}
          items={[
            { label: CIRCA.member.name, icon: 'users', onClick: () => navigate('/settings/profile') },
            { label: 'Perfil', icon: 'users', onClick: () => navigate('/settings/profile') },
            { label: 'Configuración', icon: 'settings', onClick: () => navigate('/settings') },
            { divider: true },
            { label: 'Cerrar sesión', icon: 'power', onClick: () => { void handleSignOut(); } },
          ]} />
      </div>
    </header>
  );
}

function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
  if (!open) return null;
  const ql = q.toLowerCase();
  const pages = NAV.flatMap(s => s.items).filter(i => i.label.toLowerCase().includes(ql));
  const chans = CIRCA.channels.filter(c => !q || c.name.toLowerCase().includes(ql)).slice(0, 5);
  const content = CIRCA.assets.filter(a => q && a.title.toLowerCase().includes(ql)).slice(0, 5);
  const go = (path: string) => { onClose(); navigate(path); };
  const Group = ({ title, children }: { title: string; children: ReactNode }) =>
    <div><div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', padding: '10px 16px 4px' }}>{title}</div>{children}</div>;
  const Row = ({ icon, title, sub, onClick }: { icon: string; title: string; sub?: string; onClick: () => void }) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 'none', background: 'none', cursor: 'pointer', padding: '10px 16px', textAlign: 'left' }}>
      <Icon name={icon} size={15} color="var(--muted-slate)" />
      <span style={{ flex: 1, minWidth: 0 }}><span style={{ fontSize: 14, color: 'var(--ink-muted)', display: 'block' }}>{title}</span>
        {sub && <span style={{ fontSize: 12, color: 'var(--muted-slate)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>}</span>
    </button>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1500, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '12vh' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 640, maxWidth: '92vw', background: 'var(--canvas)', borderRadius: 22, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.28)', animation: 'circa-slide-up .18s var(--ease-emphasized)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', borderBottom: '1px solid var(--border-cool)', height: 56 }}>
          <Icon name="search" size={18} color="var(--muted-slate)" />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Busca canales, contenido, programaciones o ve a una página…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 16, fontFamily: 'var(--font-sans)', background: 'transparent', color: 'var(--ink-muted)' }} />
          <span className="t-mono" style={{ fontSize: 10, background: 'var(--lightest-gray)', padding: '3px 6px', borderRadius: 4, color: 'var(--muted-slate)' }}>ESC</span>
        </div>
        <div style={{ maxHeight: 380, overflow: 'auto', paddingBottom: 8 }}>
          {chans.length > 0 && <Group title="Canales">{chans.map(c => <Row key={c.id} icon="tv" title={c.name} sub={c.slug} onClick={() => go('/channels/' + c.id)} />)}</Group>}
          {content.length > 0 && <Group title="Contenido">{content.map(a => <Row key={a.id} icon="film" title={a.title} sub={fmtDur(a.durationMs)} onClick={() => go('/library/' + a.id)} />)}</Group>}
          {pages.length > 0 && <Group title="Ir a">{pages.map(p => <Row key={p.path} icon={p.icon} title={p.label} onClick={() => go(p.path)} />)}</Group>}
          {chans.length + content.length + pages.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-slate)', fontSize: 14 }}>Sin resultados para “{q}”.</div>}
        </div>
      </div>
    </div>
  );
}

function NotificationsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [filter, setFilter] = useState('Todas');
  const { toast } = useToast();
  const [alerts, setAlerts] = useState(CIRCA.alerts);
  const filters = ['Todas', 'Sin leer', 'Alertas', 'Actividad'];
  const shown = alerts.filter(a => filter === 'Todas' ? true : filter === 'Sin leer' ? !a.read : filter === 'Alertas' ? a.sev !== 'info' : a.sev === 'info');
  const toneFor = (s: string) => s === 'critical' ? 'bad' : s === 'warning' ? 'warn' : 'info';
  return (
    <SlideOver open={open} onClose={onClose} width={420}
      title={<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400 }}>Notificaciones</h2>
        <Btn size="sm" variant="text" onClick={() => { setAlerts(a => a.map(x => ({ ...x, read: true }))); toast({ variant: 'success', message: 'Todas marcadas como leídas.' }); }}>Marcar todo leído</Btn></div>}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {filters.map(f => <button key={f} onClick={() => setFilter(f)} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer',
          background: filter === f ? 'var(--ink-muted)' : 'transparent', color: filter === f ? '#fff' : 'var(--slate)', borderColor: filter === f ? 'var(--ink-muted)' : 'var(--border-cool)',
          borderRadius: 999, padding: '5px 12px', fontSize: 13, fontFamily: 'var(--font-sans)' }}>{f}</button>)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.length === 0 && <EmptyState icon="bell" title="Nada por aquí" body="No hay notificaciones que coincidan con este filtro." />}
        {shown.map(a => (
          <div key={a.id} style={{ display: 'flex', gap: 12, padding: 14, border: '1px solid var(--lightest-gray)', borderRadius: 14, background: a.read ? 'var(--canvas)' : 'var(--pale-green)' }}>
            <Icon name={a.sev === 'critical' ? 'alertCircle' : a.sev === 'warning' ? 'alertTriangle' : 'info'} size={17} color={TONES[toneFor(a.sev)].fg} style={{ marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{a.friendly}</span>
                <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', whiteSpace: 'nowrap' }}>{ago(a.ts)}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--body-muted)', lineHeight: 1.45 }}>{a.text}</p>
              {a.channel && <span style={{ fontSize: 12, color: 'var(--muted-slate)', marginTop: 4, display: 'inline-block' }}>{a.channel}</span>}
              {!a.resolved && a.sev !== 'info' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Btn size="sm" variant="ghost" onClick={() => { setAlerts(al => al.map(x => x.id === a.id ? { ...x, read: true } : x)); }}>Reconocer</Btn>
                  <Btn size="sm" variant="text" onClick={() => { setAlerts(al => al.map(x => x.id === a.id ? { ...x, resolved: true, read: true } : x)); toast({ variant: 'success', message: 'Alerta resuelta.' }); }}>Resolver</Btn>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </SlideOver>
  );
}

/* ---------------- Shell entry point ---------------- */
export function Shell() {
  const location = useLocation();
  const navigateRR = useNavigate();
  const [palette, setPalette] = useState(false);
  const [bell, setBell] = useState(false);
  const [drawer, setDrawer] = useState(false);

  // T302 — Pattern A route-shell auth gate. Hooks (useConvexAuth, useEffect)
  // are called unconditionally on every render per the Rules of Hooks; the
  // gate decision (which branch to return) happens AFTER all hooks have
  // been called. See verification/execution_plan.md §2 for the locked
  // Pattern A shape and frontend-v3/src/hooks/useConvexConnectionState.ts
  // for the canonical `convex/react` import site (NOT `@convex-dev/auth/react`).
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();

  // Bridge legacy `navigate('/path')` callsites (lib/nav.ts) to react-router.
  useEffect(() => subscribeNavigate(path => navigateRR(path)), [navigateRR]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(p => !p);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const base = location.pathname;
  const isPublic = PUBLIC.some(p => base === p || base.startsWith(p + '/'));

  if (isPublic) {
    return (
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    );
  }

  // T302 — Non-public branch: enforce auth at the single route-shell
  // choke point (NFR-001). Three states in strict precedence:
  //   1. isLoading → skeleton (no chrome, no Outlet, no redirect). The
  //      ambiguous transient `{isAuthenticated:true, isLoading:true}`
  //      that Convex can emit during session refresh MUST also take this
  //      branch, so the loading check precedes the unauthenticated check.
  //   2. !isAuthenticated → Pattern A redirect to /sign-in with
  //      `state.from = pathname + search` so deep-link intent survives.
  //   3. Authenticated → fall through to the existing chrome + Outlet.
  if (authLoading) {
    return (
      <ToastProvider>
        <div
          data-testid="auth-loading-skeleton"
          aria-busy="true"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'var(--snow)',
            color: 'var(--muted-slate)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
          }}
        >
          <span className="circa-skeleton" style={{ width: 120, height: 32, borderRadius: 16 }} />
        </div>
      </ToastProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ToastProvider>
        <Navigate
          to="/sign-in"
          replace
          state={{ from: location.pathname + location.search }}
        />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--snow)' }}>
        <div className="circa-sidebar-wrap">{!drawer && <Sidebar path={base} />}</div>
        {drawer && <>
          <div onClick={() => setDrawer(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1099 }} />
          <Sidebar path={base} mobile onClose={() => setDrawer(false)} />
        </>}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Header path={base} onMenu={() => setDrawer(true)} onSearch={() => setPalette(true)} onBell={() => setBell(true)} />
          <main style={{ flex: 1, padding: '32px 32px 80px', maxWidth: 1320, width: '100%', margin: '0 auto' }}>
            <div className="circa-page" key={base}>
              <Outlet />
            </div>
          </main>
        </div>
        <CommandPalette open={palette} onClose={() => setPalette(false)} />
        <NotificationsDrawer open={bell} onClose={() => setBell(false)} />
      </div>
    </ToastProvider>
  );
}

export default Shell;
